// ─── GBCOSPACK — Client-side quotation PDF download ─────────────────────────
// Renders the SAME immutable QuotationDocumentData object shown in the HTML
// view into a real PDF via @react-pdf/renderer, then downloads it.
//
// The renderer + production QuotationPdfDocument + CJK fonts are all loaded via
// dynamic import so they never inflate the general Customer initial bundle.
// No Backend /pdf-stream call, no separate API re-fetch, no commercial
// recalculation — canonical document values are rendered as-is.

import { createElement } from 'react';
import type { QuotationDocumentData } from '@/quotation/document/types';
import { registerPdfFontsForLocale } from '@/quotation/pdf/fonts';

/** Internal sentinel — the public page maps this to a customer-safe message. */
export const PDF_GENERATION_FAILED = 'PDF_GENERATION_FAILED';

function buildPdfFilename(data: QuotationDocumentData): string {
  const fallback =
    data.documentType === 'final_offer'
      ? 'final-offer.pdf'
      : 'preliminary-quotation.pdf';
  const raw = (data.quotationNumber || '').trim();
  if (!raw) return fallback;
  const safe = raw.replace(/[\\/:*?"<>|]/g, '_').trim();
  return safe ? `${safe}.pdf` : fallback;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}

export async function downloadQuotationPdf(
  data: QuotationDocumentData,
): Promise<void> {
  try {
    // 1. Register only the locale-specific font (lazy CJK asset load).
    await registerPdfFontsForLocale(data.documentLocale);

    // 2. Dynamic import the PDF renderer + production document component.
    const { pdf } = await import('@react-pdf/renderer');
    const QuotationPdfDocument = (
      await import('@/quotation/pdf/QuotationPdfDocument')
    ).default;

    // 3. Render the SAME canonical data object into a PDF blob.
    const blob = await pdf(createElement(QuotationPdfDocument, { data })).toBlob();

    // 4. Trigger a browser download via a temporary object URL.
    triggerBlobDownload(blob, buildPdfFilename(data));
  } catch (err) {
    // Normalize every failure (dynamic import, font, image, render, blob,
    // download) into one customer-safe error. Never leak a raw stack trace.
    console.error('[downloadQuotationPdf] generation failed:', err);
    throw new Error(PDF_GENERATION_FAILED);
  }
}